/**
 * Service Principal Help Content
 * 
 * Feature: 136-dashboard-alignment (T056)
 * Pages: /service-principals, /service-principals/reconciliation
 * Constitution: v1.12.0
 */

import type { PageHelpContent } from '../../components/ui/PageHelpPanel';

export const servicePrincipalHelp: PageHelpContent = {
  pageTitle: 'Service Principals',
  purpose: 'Manage Entra ID service principals used for automated Fabric API access, including permission reconciliation.',
  sections: [
    {
      title: 'Service Principal Management',
      paragraphs: [
        'Service principals are Entra ID (Azure AD) application identities used by the Control Plane to authenticate with Fabric APIs. Each principal has specific API permissions assigned.',
      ],
      concepts: [
        { term: 'Service Principal', definition: 'An Entra ID application identity used for programmatic access to Fabric tenant APIs without user interaction.' },
        { term: 'API Permissions', definition: 'Microsoft Graph and Fabric API permissions granted to the service principal (e.g., Capacity.Read.All, Workspace.ReadWrite.All).' },
        { term: 'Admin Consent', definition: 'Tenant admin approval required for certain API permissions. Must be granted before the service principal can use those permissions.' },
      ],
    },
    {
      title: 'Permission Reconciliation',
      paragraphs: [
        'Reconciliation compares the expected permissions (defined in the Control Plane configuration) with the actual permissions in Entra ID. Discrepancies are flagged for review.',
      ],
      concepts: [
        { term: 'Expected Permissions', definition: 'The set of API permissions the Control Plane requires, as defined in configuration.' },
        { term: 'Actual Permissions', definition: 'The permissions currently granted to the service principal in Entra ID.' },
        { term: 'Permission Drift', definition: 'When actual permissions differ from expected — either missing required permissions or having extra unused ones.' },
      ],
    },
    {
      title: 'How To',
      steps: [
        { step: 1, instruction: 'Open the Service Principals page to view all registered principals and their current status.' },
        { step: 2, instruction: 'Click Register Principal to add a new Entra ID application identity with the required API permissions.' },
        { step: 3, instruction: 'Check the status column to verify that admin consent has been granted for each principal.' },
        { step: 4, instruction: 'Run a permission reconciliation to detect any drift between expected and actual Entra ID permissions.' },
      ],
    },
    {
      title: 'Related Pages',
      links: [
        { label: 'Admin Users', href: '/admin/users' },
        { label: 'System Settings', href: '/settings' },
        { label: 'Audit Log', href: '/audit' },
      ],
    },
  ],
};

export default servicePrincipalHelp;
